import { Heart, MapPin, Phone, Mail, MessageCircle ,  Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  return (
    <footer className="bg-gradient-to-br from-blue-900 to-blue-800 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Column */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/logo.png" 
                alt="Mains Douces Plus" 
                className="h-12 w-12 rounded-full border-2 border-amber-300"
              />
              <div>
                <h3 className="text-lg font-bold">MAINS DOUCES PLUS</h3>
                <p className="text-blue-200 text-xs">
                  {t('Healthcare Clinic Simbock', 'Clinique de Soins Simbock')}
                </p>
              </div>
            </div>
            <p className="text-sm text-blue-100 leading-relaxed mb-4">
              {t(
                'Your health is our priority. We provide quality medical care with compassion and professionalism.',
                'Votre santé est notre priorité. Nous fournissons des soins médicaux de qualité avec compassion et professionnalisme.'
              )}
            </p>
            <div className="flex items-center gap-2 text-amber-300">
              <Heart size={16} fill="currentColor" />
              <span className="text-sm italic">
                "{t('A good diagnosis for a prompt healing!', 'Un bon Diagnostic pour une Prompt Guérison!')}"
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-amber-300">
              {t('Quick Links', 'Liens Rapides')}
            </h4>
            <ul className="space-y-2">
              {[
                { path: '/', label: { en: 'Home', fr: 'Accueil' } },
                { path: '/services', label: { en: 'Services', fr: 'Services' } },
                { path: '/about', label: { en: 'About', fr: 'À Propos' } },
                { path: '/contact', label: { en: 'Contact', fr: 'Contact' } }
              ].map((item) => (
                <li key={item.path}>
                  <Link 
                    to={item.path} 
                    className="text-blue-100 hover:text-white transition flex items-center gap-2"
                  >
                    <span className="w-1 h-1 bg-amber-300 rounded-full"></span>
                    {t(item.label.en, item.label.fr)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-amber-300">
              {t('Our Services', 'Nos Services')}
            </h4>
            <ul className="space-y-2">
              {[
                { en: 'General Medicine', fr: 'Médecine Générale' },
                { en: 'Pediatrics', fr: 'Pédiatrie' },
                { en: 'Maternity', fr: 'Maternité' },
                { en: 'Laboratory', fr: 'Laboratoire' },
                { en: 'Surgery', fr: 'Chirurgie' }
              ].map((service, index) => (
                <li key={index} className="text-blue-100 flex items-center gap-2">
                  <span className="w-1 h-1 bg-amber-300 rounded-full"></span>
                  {t(service.en, service.fr)}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-amber-300">
              {t('Contact Info', 'Contact')}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin size={18} className="text-amber-300 mt-1 flex-shrink-0" />
                <span className="text-blue-100 text-sm">
                  Tradex Simbock, {t('Yaoundé, Cameroon', 'Yaoundé, Cameroun')}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Phone size={18} className="text-amber-300 mt-1 flex-shrink-0" />
                <div className="text-blue-100 text-sm">
                  <a href="tel:690640369" className="hover:text-white block">+237 690 640 369</a>
                  <a href="tel:653474614" className="hover:text-white block">+237 653 474 614</a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Clock size={18} className="text-amber-300 mt-1 flex-shrink-0" />
                <span className="text-blue-100 text-sm">
                  {t('24/7 Emergency', 'Urgence 24h/24')}
                </span>
              </li>
            </ul>

            {/* WhatsApp Button */}
            <a
              href="https://wa.me/237653474614"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition mt-4"
            >
              <MessageCircle size={16} />
              {t('WhatsApp', 'WhatsApp')}
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-blue-700">
        <div className="container mx-auto px-4 py-4">
          <p className="text-xs sm:text-sm text-center text-blue-200">
            &copy; {currentYear} {t('Clinique Mains Douces Plus - Simbock, Yaoundé', 'Clinique Mains Douces Plus - Simbock, Yaoundé')}.
            <span className="inline-flex items-center gap-1 ml-1">
              {t('Made with', 'Fait avec')} <Heart size={12} className="text-red-400" fill="currentColor" /> {t('for your health', 'pour votre santé')}
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
}